import { DefaultUserAvatar } from '@euler/app/components/DefaultUserAvatar';
import { Img } from '@euler/components/image/AdvancedImage';
import { useOrgMemberDetail } from '@euler/functions/useOrgMemberDetail';
import { memo, Suspense } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { ImageStyle } from 'react-native-fast-image';

export const useMemberAvatar = (memberId: number) => {
  const member = useOrgMemberDetail(memberId);
  return member?.avatar;
};

type PropsBase = {
  size: number;
  style?: StyleProp<ImageStyle>;
  baseStyle?: StyleProp<ImageStyle>;
  fallbackStyle?: StyleProp<ViewStyle>;
};

export const MemberAvatar = memo(
  ({
    avatar,
    size,
    style,
    baseStyle,
    fallbackStyle,
  }: {
    avatar: string | null | undefined;
  } & PropsBase) => {
    return avatar ? (
      <Img
        uri={avatar}
        css={`
          width: ${size}px;
          height: ${size}px;
        `}
        style={[baseStyle, style]}
      />
    ) : (
      <DefaultUserAvatar size={size} style={[baseStyle, fallbackStyle]} />
    );
  },
);

export const WiredMemberAvatar = memo(
  ({
    memberId,
    ...props
  }: { memberId: number | null | undefined } & PropsBase) => {
    if (memberId == null) {
      return (
        <DefaultUserAvatar size={props.size} style={props.fallbackStyle} />
      );
    }

    return (
      <Suspense
        fallback={
          <DefaultUserAvatar size={props.size} style={props.fallbackStyle} />
        }
      >
        <Content memberId={memberId} {...props} />
      </Suspense>
    );
  },
);

const Content = memo(
  ({ memberId, ...props }: { memberId: number } & PropsBase) => {
    const avatar = useMemberAvatar(memberId);
    return <MemberAvatar avatar={avatar} {...props} />;
  },
);
